import { type ReactNode, useMemo, useState } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Archive,
  ArrowRightLeft,
  BookOpen,
  CalendarClock,
  Clock,
  CreditCard,
  FormInput,
  GitBranch,
  Menu,
  Percent,
  Plug,
  Settings,
  ShieldCheck,
  Target,
  TextCursorInput,
  Webhook,
  type LucideIcon,
  Building2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { cn } from '@/lib/utils';

/**
 * CRM Settings Navigation — Information Architecture
 *
 * Was: flat horizontal tab strip with 15 tabs → overflow + truncation on
 * any screen narrower than ~1600px; effectively unusable on mobile.
 *
 * Now: 5 mental-model categories rendered as a left vertical rail on
 * desktop (≥ lg breakpoint) and a Sheet drawer on mobile. Mirrors the
 * `SettingsNavCategory[]` shape used by the general SettingsLayout
 * (frontend/src/layouts/settings/SettingsLayout.tsx) for consistency.
 *
 * Rules for future additions:
 *   - Max 5 items per category. If a category hits 6, split it.
 *   - Max 6 categories. If we hit 7, the IA is failing — rethink.
 *   - Group label is what users would say out loud, not the code module name.
 */

interface CrmSettingsNavItem {
  title: string;
  href: string;
  icon: LucideIcon;
  permission: string;
}

interface CrmSettingsNavCategory {
  label: string;
  items: CrmSettingsNavItem[];
}

const CRM_SETTINGS_NAV: CrmSettingsNavCategory[] = [
  {
    label: 'General',
    items: [
      {
        title: 'General',
        href: '/crm/settings/general',
        icon: Settings,
        permission: 'crm:setting:update:tenant',
      },
      {
        title: 'Custom Fields',
        href: '/crm/settings/custom-fields',
        icon: TextCursorInput,
        permission: 'crm:setting:update:tenant',
      },
      {
        title: 'Inventory Statuses',
        href: '/crm/settings/inventory-statuses',
        icon: TextCursorInput,
        permission: 'crm:setting:update:tenant',
      },
      {
        title: 'Business Lines',
        href: '/crm/settings/business-lines',
        icon: TextCursorInput,
        permission: 'crm:setting:update:tenant',
      },
      {
        title: 'Operating Units',
        href: '/crm/settings/business-units',
        icon: Building2,
        permission: 'crm:business-unit:read:tenant',
      },
      {
        title: 'Intake Forms',
        href: '/crm/settings/intake-forms',
        icon: FormInput,
        permission: 'crm:setting:update:tenant',
      },
    ],
  },
  {
    label: 'Sales Process',
    items: [
      {
        title: 'Pipelines',
        href: '/crm/settings/pipelines',
        icon: GitBranch,
        permission: 'crm:pipeline:update:tenant',
      },
      {
        title: 'Lead Scoring',
        href: '/crm/settings/lead-scoring',
        icon: Target,
        permission: 'crm:setting:update:tenant',
      },
      {
        title: 'SLA',
        href: '/crm/settings/sla',
        icon: Clock,
        permission: 'crm:sla:read:tenant',
      },
      {
        title: 'Approval Workflows',
        href: '/crm/settings/approval-workflows',
        icon: ShieldCheck,
        permission: 'crm:setting:update:tenant',
      },
    ],
  },
  {
    label: 'Financials',
    items: [
      {
        title: 'Payment Terms',
        href: '/crm/settings/payment-terms',
        icon: CalendarClock,
        permission: 'crm:setting:update:tenant',
      },
      {
        title: 'Payment Methods',
        href: '/crm/settings/payment-methods',
        icon: CreditCard,
        permission: 'crm:setting:update:tenant',
      },
      {
        title: 'Tax',
        href: '/crm/settings/tax',
        icon: Percent,
        permission: 'crm:setting:update:tenant',
      },
      {
        title: 'Exchange Rates',
        href: '/crm/settings/exchange-rates',
        icon: ArrowRightLeft,
        permission: 'crm:setting:update:tenant',
      },
      {
        title: 'Price Books',
        href: '/crm/settings/price-books',
        icon: BookOpen,
        permission: 'crm:setting:update:tenant',
      },
    ],
  },
  {
    label: 'Integrations',
    items: [
      {
        title: 'Webhooks',
        href: '/crm/settings/webhooks',
        icon: Webhook,
        permission: 'crm:setting:update:tenant',
      },
      {
        title: 'Integrations',
        href: '/crm/settings/integrations',
        icon: Plug,
        permission: 'crm:setting:read:tenant',
      },
    ],
  },
  {
    label: 'Data',
    items: [
      {
        title: 'Archive',
        href: '/crm/settings/archive',
        icon: Archive,
        permission: 'crm:archive:manage:tenant',
      },
    ],
  },
];

interface CrmSettingsLayoutProps {
  children: ReactNode;
}

export function CrmSettingsLayout({ children }: CrmSettingsLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Filter each category's items by permission. Drop categories that end up
  // empty so they don't render as a bare label.
  const visibleCategories = useMemo(
    () =>
      CRM_SETTINGS_NAV.map((cat) => ({
        ...cat,
        items: cat.items.filter((item) => hasPermission(item.permission)),
      })).filter((cat) => cat.items.length > 0),
    [hasPermission],
  );

  const isItemMatch = (pathname: string, href: string) =>
    pathname === href || pathname.startsWith(href + '/');

  const activeItem = useMemo(() => {
    for (const cat of visibleCategories) {
      const match = cat.items.find((item) => isItemMatch(location.pathname, item.href));
      if (match) return match;
    }
    return undefined;
  }, [visibleCategories, location.pathname]);

  const handleNavigate = (href: string) => {
    setMobileNavOpen(false);
    void navigate({ to: href });
  };

  // Reusable nav list — rendered both in the desktop left rail and the
  // mobile Sheet drawer so the two surfaces share the same structure + a11y.
  const NavTree = (
    <nav className="space-y-6">
      {visibleCategories.map((cat) => (
        <div key={cat.label}>
          <h3 className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {cat.label}
          </h3>
          <ul className="space-y-1">
            {cat.items.map((item) => {
              const Icon = item.icon;
              const isActive = activeItem?.href === item.href;
              return (
                <li key={item.href}>
                  <button
                    type="button"
                    onClick={() => handleNavigate(item.href)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors',
                      'text-left', // override <button> default centering
                      isActive
                        ? 'bg-accent font-medium text-accent-foreground'
                        : 'text-foreground hover:bg-accent/50',
                    )}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" aria-hidden />
                    <span className="truncate">{item.title}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );

  return (
    <div className="flex-1">
      {/* Page header — title + back button. Always visible at top. */}
      <div className="mb-6 flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: '/crm' })}
          aria-label="Back to CRM"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-2xl font-bold tracking-tight sm:text-3xl">CRM Settings</h2>
          <p className="text-sm text-muted-foreground">
            Configure your CRM module settings and preferences
          </p>
        </div>

        {/* Mobile-only: open nav drawer. Hidden on lg+ where the rail is visible. */}
        <Button
          variant="outline"
          size="sm"
          className="lg:hidden flex-shrink-0"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open settings menu"
        >
          <Menu className="h-4 w-4" />
          <span className="ml-2 hidden sm:inline">{activeItem?.title ?? 'Menu'}</span>
        </Button>
      </div>

      {/* Body: 2-column on lg+, single-column on mobile. */}
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Desktop left rail.

            Note on sticky: the rail is INTENTIONALLY non-sticky here even
            though sticky would be a UX improvement. DashboardLayout's root
            wrapper sets `overflow-x: hidden`, which per CSS Overflow Module
            Level 3 creates a scroll container — that captures sticky
            descendants' scrolling-ancestor traversal and prevents them
            from sticking to the document. To make this rail sticky we'd
            first need to change that to `overflow-x: clip`, which is a
            separate concern and a wider blast radius than this PR.
            Future enhancement: add `lg:sticky lg:top-16 lg:self-start`
            once the root overflow is fixed. */}
        <aside className="hidden w-64 flex-shrink-0 lg:block">{NavTree}</aside>

        {/* Mobile Sheet drawer — opens via the Menu button above. */}
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetContent side="left" className="w-72 overflow-y-auto">
            <SheetHeader>
              <SheetTitle>CRM Settings</SheetTitle>
            </SheetHeader>
            <div className="mt-6">{NavTree}</div>
          </SheetContent>
        </Sheet>

        {/* Content column. min-w-0 prevents flex children (wide tables, code
            blocks) from blowing out the layout horizontally. */}
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </div>
  );
}
